/**
 * "Added to cart" popup.
 *
 * Shows the item that was just added plus a "Complete your look with" block with two complementary
 * products and a size picker.
 *
 * Lifecycle:
 * - "variant:adding" (custom.js addToCart helper) or a theme product form submit opens the popup at
 *   once, with shimmer placeholders that have the exact size of the content they stand for.
 * - "variant:added" (theme product form in theme.js, or the addToCart helper) fills the header from the
 *   /cart/add.js response and loads the recommendations. Images keep their shimmer until loaded.
 *
 * Adds made while the mini cart drawer is open are ignored: the drawer already shows them.
 *
 * Full looks (component-look.js, "Add full look") are announced with "look:adding" just before the add: the
 * popup then shows the look title, photo and number of items instead of its first product, and no
 * recommendations — the customer has just taken a whole look.
 */
class AddedToCartPopup extends HTMLElement {
  static PENDING_TIMEOUT = 8000;
  static TRANSITION_DURATION = 350;

  connectedCallback() {
    this.messageElement = this.querySelector('.added-popup__message');
    this.thumbElement = this.querySelector('.added-popup__thumb');
    this.imageElement = this.querySelector('.added-popup__image');
    this.recommendationsElement = this.querySelector('.added-popup__recommendations');
    this.requestId = 0;

    // The Liquid markup ships the skeleton: keep a copy to restore it before every load.
    this.messageSkeletonHtml = this.messageElement.innerHTML;
    this.recommendationsSkeletonHtml = this.recommendationsElement ? this.recommendationsElement.innerHTML : '';

    document.addEventListener('look:adding', (event) => { this.pendingLook = event.detail || null; });
    document.addEventListener('variant:adding', this.onVariantAdding.bind(this));
    document.addEventListener('submit', this.onFormSubmit.bind(this));
    document.addEventListener('variant:added', this.onVariantAdded.bind(this));
    document.addEventListener('cart-notification:show', this.onCartNotification.bind(this));
    document.addEventListener('openable-element:open', (event) => {
      if (event.target && event.target.id === 'mini-cart') {
        this.close();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
      }
    });
    document.addEventListener('click', this.onDocumentClick.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
  }

  // Kept for compatibility with the theme "toggle-button" elements (aria-controls="product-added-popup").
  get open() {
    return !this.hidden;
  }

  set open(value) {
    if (value) {
      this.show();
    } else {
      this.close();
    }
  }

  get miniCart() {
    return document.getElementById('mini-cart');
  }

  get isDrawerOpen() {
    return this.miniCart !== null && this.miniCart.hasAttribute('open');
  }

  /* ---------------------------------------------------------------------------------------------
     Opening / closing
     --------------------------------------------------------------------------------------------- */

  show() {
    clearTimeout(this.closeTimeout);

    if (!this.hidden && this.classList.contains('added-popup--visible')) {
      return;
    }

    this.hidden = false;
    this.scrollTop = 0;
    this.shownAt = performance.now();

    // Force a style flush of the hidden state so the entrance transition always plays.
    void this.offsetHeight;
    this.classList.add('added-popup--visible');
  }

  close() {
    if (this.hidden) {
      return;
    }

    clearTimeout(this.pendingTimeout);
    clearTimeout(this.closeTimeout);
    this.pendingLook = null;
    this.classList.remove('added-popup--visible');
    this.closePickers();

    this.closeTimeout = setTimeout(() => {
      this.hidden = true;
    }, AddedToCartPopup.TRANSITION_DURATION);
  }

  /* ---------------------------------------------------------------------------------------------
     Add to cart lifecycle
     --------------------------------------------------------------------------------------------- */

  onFormSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') {
      return;
    }

    if (!form.checkValidity() || form.closest('#mini-cart')) {
      return;
    }

    this.openPending();
  }

  onVariantAdding() {
    if (this.isDrawerOpen) {
      return;
    }

    this.openPending();
  }

  // Opens the popup with every slot in its loading state.
  openPending() {
    this.requestId++;
    this.renderHeaderSkeleton();

    if (this.pendingLook) {
      this.hideRecommendations();
    } else {
      this.renderRecommendationsSkeleton();
    }

    this.show();

    clearTimeout(this.pendingTimeout);
    this.pendingTimeout = setTimeout(() => {
      if (this.messageElement.querySelector('.added-popup__shimmer')) {
        this.close();
      }
    }, AddedToCartPopup.PENDING_TIMEOUT);
  }

  onVariantAdded(event) {
    const item = event.detail && event.detail.variant;

    if (!item || this.isDrawerOpen) {
      // A look announced for an add the popup ignores must not leak into the next one
      this.pendingLook = null;
      return;
    }

    clearTimeout(this.pendingTimeout);

    if (this.pendingLook) {
      const look = this.pendingLook;

      this.pendingLook = null;
      this.requestId++;
      this.renderLookHeader(look);
      this.hideRecommendations();
      this.show();
      return;
    }

    this.renderHeader(item);
    this.renderRecommendationsSkeleton();
    this.show();
    this.loadRecommendations(item);
  }

  // The theme reports failed adds through this event: nothing to show then.
  onCartNotification(event) {
    if (event.detail && event.detail.status === 'error' && this.messageElement.querySelector('.added-popup__shimmer')) {
      this.close();
    }
  }

  /* ---------------------------------------------------------------------------------------------
     Header
     --------------------------------------------------------------------------------------------- */

  renderHeaderSkeleton() {
    this.messageElement.classList.remove('added-popup__message--enter');
    this.messageElement.innerHTML = this.messageSkeletonHtml;
    this.thumbElement.classList.add('added-popup__thumb--loading');
    this.imageElement.removeAttribute('src');
    this.imageElement.removeAttribute('srcset');
    this.imageElement.alt = '';
  }

  renderHeader(item) {
    const title = this.escapeHtml(item.product_title || item.title || '');
    const hasVariantTitle = item.variant_title && item.variant_title !== 'Default Title';
    const template = hasVariantTitle ? this.dataset.messageHtml : this.dataset.messageNoVariantHtml;

    this.messageElement.innerHTML = template
      .replace('[title]', title)
      .replace('[variant]', hasVariantTitle ? this.escapeHtml(item.variant_title) : '');

    // Restart the fade-in of the message (also when the popup is already open and re-renders).
    this.messageElement.classList.remove('added-popup__message--enter');
    void this.messageElement.offsetWidth;
    this.messageElement.classList.add('added-popup__message--enter');

    const imageUrl = (item.featured_image && item.featured_image.url) || item.image;

    this.thumbElement.classList.add('added-popup__thumb--loading');

    if (imageUrl) {
      this.imageElement.src = this.resizeImage(imageUrl, 160);
      this.imageElement.srcset = `${this.resizeImage(imageUrl, 160)} 1x, ${this.resizeImage(imageUrl, 320)} 2x`;
      this.imageElement.alt = (item.featured_image && item.featured_image.alt) || '';
      this.revealWhenLoaded(this.imageElement, this.thumbElement, 'added-popup__thumb--loading');
    } else {
      this.imageElement.removeAttribute('src');
      this.imageElement.removeAttribute('srcset');
    }
  }

  // Full look: "<look> was added to cart (4 items)" with the look photo
  renderLookHeader(look) {
    this.messageElement.innerHTML = this.dataset.messageLookHtml
      .replace('[title]', this.escapeHtml(look.title || ''))
      .replace('[items]', this.escapeHtml(look.items || ''));

    this.messageElement.classList.remove('added-popup__message--enter');
    void this.messageElement.offsetWidth;
    this.messageElement.classList.add('added-popup__message--enter');

    this.thumbElement.classList.add('added-popup__thumb--loading');

    if (look.image) {
      this.imageElement.src = look.image;
      this.imageElement.srcset = look.image2x ? `${look.image} 1x, ${look.image2x} 2x` : '';
      this.imageElement.alt = look.title || '';
      this.revealWhenLoaded(this.imageElement, this.thumbElement, 'added-popup__thumb--loading');
    } else {
      this.imageElement.removeAttribute('src');
      this.imageElement.removeAttribute('srcset');
    }
  }

  /* ---------------------------------------------------------------------------------------------
     Recommendations
     --------------------------------------------------------------------------------------------- */

  hideRecommendations() {
    if (this.recommendationsElement) {
      this.recommendationsElement.hidden = true;
    }
  }

  renderRecommendationsSkeleton() {
    // Block not rendered when disabled in theme settings (Cart > Show recommendations).
    if (!this.recommendationsElement) {
      return;
    }

    this.recommendationsElement.hidden = false;
    this.recommendationsElement.innerHTML = this.recommendationsSkeletonHtml;
  }

  async loadRecommendations(item) {
    if (!this.recommendationsElement) {
      return;
    }

    const requestId = ++this.requestId;

    if (!item.product_id) {
      this.recommendationsElement.hidden = true;
      return;
    }

    // Same products as the "Complete the look" strip of the product page; related products when it has none,
    // so the block never stays empty.
    let html = item.handle ? await this.fetchLookProducts(item.handle) : '';

    if (!html && requestId === this.requestId) {
      html = await this.fetchRecommendations(item.product_id, 'related');
    }

    if (requestId !== this.requestId) {
      return;
    }

    if (!html) {
      this.recommendationsElement.hidden = true;
      return;
    }

    this.recommendationsElement.innerHTML = html;
    this.recommendationsElement.querySelectorAll('.added-popup__media--loading img').forEach((image) => {
      this.revealWhenLoaded(image, image.closest('.added-popup__media'), 'added-popup__media--loading');
    });
  }

  // The section rendered on the product URL reads its "Style with" metafield
  async fetchLookProducts(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({ section_id: this.dataset.recommendationsSection });

    return this.fetchSection(`${root}products/${encodeURIComponent(handle)}?${params}`);
  }

  async fetchRecommendations(productId, intent) {
    const params = new URLSearchParams({
      section_id: this.dataset.recommendationsSection,
      product_id: productId,
      limit: 4,
      intent: intent
    });

    return this.fetchSection(`${this.dataset.recommendationsUrl}?${params}`);
  }

  async fetchSection(url) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        return '';
      }

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const inner = parsed.querySelector('.added-popup__recommendations-inner');

      return inner ? inner.outerHTML : '';
    } catch (error) {
      return '';
    }
  }

  /* ---------------------------------------------------------------------------------------------
     Interactions
     --------------------------------------------------------------------------------------------- */

  onClick(event) {
    const actionElement = event.target.closest('[data-action]');

    if (!actionElement) {
      return;
    }

    switch (actionElement.dataset.action) {
      case 'close':
        this.close();
        break;

      case 'go-to-cart':
        // Opens the mini cart drawer like the header icon; the link itself is the fallback.
        if (this.miniCart) {
          event.preventDefault();
          this.close();
          this.miniCart.open = true;
        }
        break;

      case 'toggle-picker':
        this.togglePicker(actionElement.closest('.added-popup__product'));
        break;

      case 'add':
        this.addVariant(actionElement);
        break;
    }
  }

  onDocumentClick(event) {
    // composedPath() is captured at dispatch time, so a click on a size button that the skeleton
    // has already replaced still counts as a click inside the popup.
    if (this.hidden || event.composedPath().includes(this)) {
      return;
    }

    // The click that triggered the add to cart reaches the document right after the popup opened:
    // it must not count as a click outside.
    if (performance.now() - this.shownAt < 500) {
      return;
    }

    this.close();
  }

  togglePicker(productElement) {
    const isOpen = productElement.classList.contains('added-popup__product--picking');

    this.closePickers();

    if (!isOpen) {
      this.setPickerState(productElement, true);
    }
  }

  closePickers() {
    this.querySelectorAll('.added-popup__product--picking').forEach((productElement) => {
      this.setPickerState(productElement, false);
    });
  }

  setPickerState(productElement, isOpen) {
    const picker = productElement.querySelector('.added-popup__picker');
    const toggle = productElement.querySelector('[data-action="toggle-picker"]');

    productElement.classList.toggle('added-popup__product--picking', isOpen);

    if (picker) {
      picker.hidden = !isOpen;
    }

    if (toggle) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  async addVariant(button) {
    const variantId = button.dataset.variantId;

    if (!variantId || button.hasAttribute('aria-busy')) {
      return;
    }

    button.setAttribute('aria-busy', 'true');

    try {
      if (typeof addToCart === 'function') {
        // custom.js helper: refreshes the minicart and fires "variant:adding" / "variant:added".
        await addToCart({ id: variantId, quantity: 1 }, true, false, false);
      } else {
        await this.addToCartFallback(variantId);
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  async addToCartFallback(variantId) {
    this.openPending();

    const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, sections: ['mini-cart'] })
    });

    if (!response.ok) {
      this.close();
      return;
    }

    const item = await response.json();

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('variant:added', {
      bubbles: true,
      detail: { variant: item, status: 'success' }
    }));
  }

  /* ---------------------------------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------------------------------- */

  // Removes the shimmer class from the wrapper once the image has actually painted.
  revealWhenLoaded(image, wrapper, loadingClass) {
    const reveal = () => wrapper.classList.remove(loadingClass);

    if (image.complete && image.naturalWidth > 0) {
      reveal();
      return;
    }

    image.addEventListener('load', reveal, { once: true });
    image.addEventListener('error', reveal, { once: true });
  }

  resizeImage(url, width) {
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

window.customElements.define('added-to-cart-popup', AddedToCartPopup);
